import './OutreachContainer.scss';
import React, { FunctionComponent, memo, useEffect } from 'react';
import { notify } from 'plume-ui';
import { useRedirectToRoute } from '../../../utils/hooks/useRedirectToRoute';
import { Routes } from '../../../config';

import { Formik } from 'formik';
import useOutreach, {
  OutreachConfigFormDto,
} from '../../Outreach/hooks/useOutreach';
import { useParams } from 'react-router-dom';
import useValidation from '../components/OutreachConfigForm/hooks/useValidation';
import { useTranslation } from 'react-i18next';

import OutreachFormRenderer from '../components/OutreachConfigForm/controls/OutreachConfig/OutreachFormRenderer';
import { AxiosError } from 'axios';
import { useTrackEvent } from '../../trackingAnalytics/hooks/useTrackEvent';
import { MixPanelEvents } from '../../../mixPanelEvents';
import { OutreachTypes } from '../../../urls';
import {
  MonitorsTab,
  OutreachConfigFormFields,
  OutreachConfigSections,
} from '../types';
import { AvailableScreens } from '../../trackingAnalytics/types';
import { ErrorResponse } from '../../../types';

const OutreachContainer: FunctionComponent = () => {
  const redirectToRoute = useRedirectToRoute();
  const { partnerId, monitorId } = useParams<{
    partnerId: string;
    monitorId: string;
  }>();
  const { t } = useTranslation();

  const trackEvent = useTrackEvent();

  const goBack = () => {
    redirectToRoute(Routes.MonitorsTab, {
      partnerId,
      tab: MonitorsTab.User,
    });
  };

  const getAdditionalContentForFailure = (
    errorMessage: string,
    type: OutreachTypes,
    values: OutreachConfigFormDto,
  ) => {
    const config = values[OutreachConfigSections.Config];
    const isConfigEnabled =
      type === OutreachTypes.Push
        ? config[OutreachConfigFormFields.SubscriberOutreachEnabled]
        : config[OutreachConfigFormFields.SupportOutreachEnabled];
    const baseAdditionalContent = {
      MONITOR_ID: monitorId,
      PARTNER_ID: partnerId,
      ERROR_MESSAGE: errorMessage,
      ENABLED: isConfigEnabled,
    };
    return type === OutreachTypes.Push
      ? {
          ...baseAdditionalContent,
          subject:
            values[OutreachConfigSections.SubscriberOutreachContent]?.subject ||
            '',
          message:
            values[OutreachConfigSections.SubscriberOutreachContent]?.message ||
            '',
        }
      : baseAdditionalContent;
  };

  const onCreateFailure = (
    type: OutreachTypes,
    error: AxiosError<ErrorResponse>,
    values: OutreachConfigFormDto,
  ) => {
    const body =
      error?.response?.data?.message || t('somethingWentWrong') || '';
    const eventName =
      type === OutreachTypes.Email
        ? MixPanelEvents.OUTREACH_CONFIG_SUPPORT_CREATE_FAILURE
        : MixPanelEvents.OUTREACH_CONFIG_SUBSCRIBER_CREATE_FAILURE;
    const additionalContent = getAdditionalContentForFailure(
      body,
      type,
      values,
    );
    trackEvent({
      eventName,
      additionalContent,
    });
    notify({
      title: t('error'),
      body,
      type: 'error',
    });
  };

  const onUpdateFailure = (
    type: OutreachTypes,
    error: AxiosError<ErrorResponse>,
    values: OutreachConfigFormDto,
  ) => {
    const body =
      error?.response?.data?.message || t('somethingWentWrong') || '';
    const eventName =
      type === OutreachTypes.Email
        ? MixPanelEvents.OUTREACH_CONFIG_SUPPORT_UPDATE_FAILURE
        : MixPanelEvents.OUTREACH_CONFIG_SUBSCRIBER_UPDATE_FAILURE;
    const additionalContent = getAdditionalContentForFailure(
      body,
      type,
      values,
    );
    trackEvent({
      eventName,
      additionalContent,
    });
    notify({
      title: t('error'),
      body,
      type: 'error',
    });
  };

  const onUpdateSupportSuccess = (
    type: OutreachTypes,
    values: OutreachConfigFormDto,
  ) => {
    const config = values[OutreachConfigSections.Config];
    trackEvent({
      eventName: MixPanelEvents.OUTREACH_CONFIG_SUPPORT_UPDATE_SUCCESS,
      additionalContent: {
        MONITOR_ID: monitorId,
        PARTNER_ID: partnerId,
        ENABLED: config[OutreachConfigFormFields.SupportOutreachEnabled],
      },
    });
    notify({
      title: t('success'),
      body: t('outreach.form.resultMessages.outreachConfigUpdated'),
      type: 'success',
    });
  };

  const onCreateSupportSuccess = (
    type: OutreachTypes,
    values: OutreachConfigFormDto,
  ) => {
    const config = values[OutreachConfigSections.Config];
    trackEvent({
      eventName: MixPanelEvents.OUTREACH_CONFIG_SUPPORT_CREATE_SUCCESS,
      additionalContent: {
        MONITOR_ID: monitorId,
        PARTNER_ID: partnerId,
        ENABLED: config[OutreachConfigFormFields.SupportOutreachEnabled],
      },
    });
    notify({
      title: t('success'),
      body: t('outreach.form.resultMessages.outreachConfigCreated'),
      type: 'success',
    });
  };

  const onUpdateSubscriberSuccess = (
    type: OutreachTypes,
    values: OutreachConfigFormDto,
  ) => {
    const config = values[OutreachConfigSections.Config];
    trackEvent({
      eventName: MixPanelEvents.OUTREACH_CONFIG_SUBSCRIBER_UPDATE_SUCCESS,
      additionalContent: {
        MONITOR_ID: monitorId,
        PARTNER_ID: partnerId,
        ENABLED: config[OutreachConfigFormFields.SubscriberOutreachEnabled],
        subject:
          values[OutreachConfigSections.SubscriberOutreachContent]?.subject ||
          '',
        message:
          values[OutreachConfigSections.SubscriberOutreachContent]?.message ||
          '',
      },
    });
    notify({
      title: t('success'),
      body: t('outreach.form.resultMessages.outreachConfigUpdated'),
      type: 'success',
    });
  };

  const onCreateSubscriberSuccess = (
    type: OutreachTypes,
    values: OutreachConfigFormDto,
  ) => {
    const config = values[OutreachConfigSections.Config];
    trackEvent({
      eventName: MixPanelEvents.OUTREACH_CONFIG_SUBSCRIBER_CREATE_SUCCESS,
      additionalContent: {
        MONITOR_ID: monitorId,
        PARTNER_ID: partnerId,
        ENABLED: config[OutreachConfigFormFields.SubscriberOutreachEnabled],
        subject:
          values[OutreachConfigSections.SubscriberOutreachContent]?.subject ||
          '',
        message:
          values[OutreachConfigSections.SubscriberOutreachContent]?.message ||
          '',
      },
    });
    notify({
      title: t('success'),
      body: t('outreach.form.resultMessages.outreachConfigCreated'),
      type: 'success',
    });
  };

  const onFinalize = () => {
    redirectToRoute(Routes.MonitorsTab, {
      partnerId,
      tab: MonitorsTab.User,
    });
  };

  const onRetrieveConfigError = (error: AxiosError<ErrorResponse>) => {
    const statusCode = error?.response?.status;
    const body =
      error?.response?.data?.message || t('somethingWentWrong') || '';
    trackEvent({
      eventName: MixPanelEvents.OUTREACH_CONFIG_RETRIEVE_FAILURE,
      additionalContent: {
        MONITOR_ID: monitorId,
        PARTNER_ID: partnerId,
        ERROR_MESSAGE: body,
      },
    });
    if (statusCode === 403) {
      redirectToRoute(Routes.MonitorsTab, {
        partnerId,
        tab: MonitorsTab.User,
      });
      return;
    }
  };

  const {
    loadingMetadata,
    getFormInitialValues,
    isError,
    onSubmit,
    isSubmitting,
    flushConfig,
  } = useOutreach({
    supportEmail: {
      onCreateFailure,
      onUpdateFailure,
      onUpdateSuccess: onUpdateSupportSuccess,
      onCreateSuccess: onCreateSupportSuccess,
    },
    supportWebhook: {
      onCreateFailure,
      onUpdateFailure,
      onUpdateSuccess: onUpdateSupportSuccess,
      onCreateSuccess: onCreateSupportSuccess,
    },
    subscriber: {
      onCreateFailure,
      onUpdateFailure,
      onUpdateSuccess: onUpdateSubscriberSuccess,
      onCreateSuccess: onCreateSubscriberSuccess,
    },
    onFinalize: onFinalize,
    onRetrieveConfigError,
  });
  const { getFormikValidationSchema } = useValidation();

  useEffect(() => {
    trackEvent({
      eventName: MixPanelEvents.SCREEN,
      additionalContent: {
        SCREEN: AvailableScreens.OutreachConfig,
      },
    });
    return () => {
      flushConfig();
    };
  }, []);

  return (
    <div className="OutreachContainer">
      <Formik
        initialValues={getFormInitialValues()}
        enableReinitialize
        validationSchema={getFormikValidationSchema()}
        validateOnChange={false}
        onSubmit={onSubmit}
      >
        <OutreachFormRenderer
          loadingMetadata={loadingMetadata}
          isError={isError}
          goBack={goBack}
          isSubmitting={isSubmitting}
        />
      </Formik>
    </div>
  );
};

export default memo(OutreachContainer);
